import { Box, Typography } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useScheduleEditorDraftTokens } from "./hooks/use-schedule-editor-draft-tokens"
import { Comparison } from "routes/schedule/compare/lib/diff3"
import { expandMacroTokens } from "authoring/routes/macro/lib/macro-text-utils"
import { buildStructuredRichTextPreviewHtml, resolveRichTextPreviewMode } from "./lib/rich-text-comparison-presenters"
import { StructuredRichTextPreview } from "./structured-rich-text-preview"

const COMPARISON_OL_LEVEL_ONE_WIDTH = 18
const COMPARISON_OL_LEVEL_TWO_WIDTH = 24
const COMPARISON_OL_LEVEL_THREE_WIDTH = 30

export function FlatRichTextPreview({
    hasContent,
    dataCy,
    original,
    other,
    text,
    showRemoved = false,
    sourceOriginalValue,
    sourceTailoredValue,
    previewSide,
}) {
    const { macroMap: sessionMacroMap } = useScheduleEditorDraftTokens()
    const { macroMap: boundMacroMap } = useBoundContext()
    const macroMap = Object.keys(sessionMacroMap ?? {}).length ? sessionMacroMap : boundMacroMap ?? {}

    if (!hasContent) {
        return (
            <Typography variant="body2" color="text.disabled" data-cy={dataCy}>
                (empty)
            </Typography>
        )
    }

    const previewMode = resolveRichTextPreviewMode({
        originalValue: sourceOriginalValue ?? original,
        tailoredValue: sourceTailoredValue ?? text,
    })

    if (previewMode === "structured") {
        return (
            <StructuredRichTextPreview
                dataCy={dataCy}
                html={buildStructuredRichTextPreviewHtml({
                    originalValue: expandMacroTokens(sourceOriginalValue ?? original, macroMap),
                    tailoredValue: expandMacroTokens(sourceTailoredValue ?? text, macroMap),
                    side: previewSide,
                })}
            />
        )
    }

    return (
        <Box
            className="styled-bullets"
            sx={{
                fontSize: 12,
                lineHeight: 1.5,
                color: "text.secondary",
                wordBreak: "break-word",
                "& p": {
                    fontSize: "inherit",
                    lineHeight: "inherit",
                    color: "inherit",
                    m: 0,
                },
                "& ul, & ol": {
                    my: 0,
                },
                "& ol": {
                    paddingInlineStart: `${COMPARISON_OL_LEVEL_ONE_WIDTH}px`,
                },
                "& ol > li::before": {
                    insetInlineStart: `-${COMPARISON_OL_LEVEL_ONE_WIDTH}px`,
                    width: `${COMPARISON_OL_LEVEL_ONE_WIDTH}px`,
                },
                "& ol > li > ol": {
                    paddingInlineStart: `${COMPARISON_OL_LEVEL_TWO_WIDTH}px`,
                },
                "& ol > li > ol > li::before": {
                    insetInlineStart: `-${COMPARISON_OL_LEVEL_TWO_WIDTH}px`,
                    width: `${COMPARISON_OL_LEVEL_TWO_WIDTH}px`,
                },
                "& ol > li > ol > li > ol": {
                    paddingInlineStart: `${COMPARISON_OL_LEVEL_THREE_WIDTH}px`,
                },
                "& ol > li > ol > li > ol > li::before": {
                    insetInlineStart: `-${COMPARISON_OL_LEVEL_THREE_WIDTH}px`,
                    width: `${COMPARISON_OL_LEVEL_THREE_WIDTH}px`,
                },
                "& li": {
                    fontSize: "inherit",
                    lineHeight: "inherit",
                    color: "inherit",
                },
            }}
            data-cy={dataCy}
        >
            <Comparison original={original} other={other} text={text} showRemoved={showRemoved} />
        </Box>
    )
}
